<script setup lang="ts">
import LoadingModal from "@/components/LoadingModal.vue";
import RoundedBox from "@/components/RoundedBox.vue";
import StyledSwitch from "@/components/StyledSwitch.vue";
import useCompany from "@/modules/companies/composables/useCompany";
import useCompanyCredit from "@/modules/companies/composables/useCompanyCredit";
import { CompanyEditTab } from "@/modules/companies/interfaces/companyEditTab";
import { useQueryClient } from "@tanstack/vue-query";
import { useDebounceFn } from "@vueuse/core";
import { computed, inject, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { useToast } from "vue-toastification";

interface MethodPayment {
  name: string;
  enabled: boolean;
  value: string;
  disable?: boolean;
}

interface Props extends CompanyEditTab {}

const props = defineProps<Props>();
const route = useRoute();

const {
  updateCompanyAsync,
  company,
  isLoading: loadingCompany,
} = useCompany(
  {
    companyId: Number(route.params.id),
  },
  {
    immediate: true,
  },
);

const { credit, isLoading } = useCompanyCredit({
  params: {
    companyId: Number(route.params.id),
  },
  immediate: true,
});

const queryClient = useQueryClient();

const toast = useToast();

const methodsPayments = ref<MethodPayment[]>([
  {
    name: "Crédito empresarial",
    enabled: false,
    value: "credit",
    disable: true,
  },
  {
    name: "Tarjeta",
    enabled: false,
    value: "card",
    disable: false,
  },
]);

// Computed para determinar si el crédito está deshabilitado
const isCreditDisabled = computed(() => {
  // Deshabilitado si no existe crédito o si el crédito está eliminado (deleted)
  return !credit.value || credit.value?.deleted;
});

// Watch para actualizar los valores cuando los datos carguen
watch(
  [company, credit],
  () => {
    if (company.value) {
      // Actualizar crédito empresarial
      methodsPayments.value[0].enabled =
        company.value.paymentMethods?.options.includes("credit") ?? false;
      methodsPayments.value[0].disable = isCreditDisabled.value;

      // Actualizar tarjeta
      methodsPayments.value[1].enabled =
        company.value.paymentMethods?.options.includes("card") ?? false;
    }
  },
  { immediate: true },
);

const { invalidateQueryData } = inject("editCompanyProvides") as {
  invalidateQueryData: () => Promise<void>;
};

const handleUpdateEnabled = async (i: number) => {
  if (methodsPayments.value[i].disable) {
    methodsPayments.value[i].enabled = false;
    toast.error(
      credit.value
        ? "No puedes habilitar este método de pago"
        : "Para habilitar esta opción la empresa debe de tener un crédito asignado",
    );
    return;
  }

  const methodsPayload = methodsPayments.value
    .filter((method) => (method.enabled ? method.value : null))
    .map((method) => method.value);

  if (methodsPayload.length === 0) {
    methodsPayments.value[i].enabled = true;
    toast.error("Debes tener al menos un método de pago seleccionado");
    return;
  }

  try {
    await updateCompanyAsync({
      companyId: props.company.id!,
      paymentMethods: {
        options: methodsPayload,
      },
    });
  } catch (error) {
    toast.error("Error al actualizar los métodos de pago");
  } finally {
    await Promise.all([
      queryClient.invalidateQueries(["company", props.company.id]),
      queryClient.invalidateQueries(["companyCredit", Number(route.params.id)]),
      invalidateQueryData(),
    ]);
  }
};

const debounceClick = useDebounceFn(handleUpdateEnabled, 800);
</script>

<template>
  <RoundedBox class="bg-white" v-if="!isLoading || !loadingCompany">
    <h1 class="font-medium text-xl">Métodos de pagos permitido del clientes</h1>

    <ul class="pt-5">
      <li>
        <div
          class="flex justify-between items-center gap-2 py-2 border-b border-gray-200"
          v-for="(methodPayment, i) in methodsPayments"
          :key="methodPayment.name"
        >
          <h2>{{ methodPayment.name }}</h2>
          <StyledSwitch
            v-tooltip="
              methodPayment.disable
                ? 'Crédito desactivado - Actívalo primero en la pestaña de Crédito'
                : methodPayment.enabled
                  ? `Desactivar el método de pago: ${methodPayment.name}`
                  : `Activar el método de pago: ${methodPayment.name}`
            "
            v-model:enabled="methodsPayments[i].enabled"
            :disabled="methodPayment.disable"
            @update:enabled="() => debounceClick(i)"
          />
        </div>
      </li>
    </ul>
  </RoundedBox>

  <LoadingModal :when="isLoading" />
</template>
